import { Fragment, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import api, { getImageUrl } from '@/lib/api';
import { toast } from 'sonner';
import { Check, Crown, Eye, FolderOpen, GitMerge, Loader2, MapPin, Trash2, Users, X } from 'lucide-react';

const pluralKind = (kind) => (kind === 'character' ? 'characters' : 'locations');
const imgOf = (kind, x) => (kind === 'character' ? x.image_storage_path : x.image_path);
const IMAGE_FIELDS = new Set(['image_storage_path', 'image_path']);
const FIELD_LABELS = { description: 'Description', image_storage_path: 'Reference image', image_path: 'Reference image', character_type: 'Type', tags: 'Tags',
  voice_id: 'ElevenLabs voice', voice_name: 'Voice name', kling_voice_id: 'Native video voice', kling_voice_name: 'Native voice name', voice_description: 'Voice profile' };

function DiffValue({ field, value }) {
  if (IMAGE_FIELDS.has(field)) {
    return value ? <img src={getImageUrl(value)} alt="" className="w-12 h-12 rounded object-cover border border-white/10" /> : <span className="text-[#55555C] italic">none</span>;
  }
  if (Array.isArray(value)) return <span>{value.length ? value.join(', ') : <span className="text-[#55555C] italic">none</span>}</span>;
  const s = value == null || value === '' ? null : String(value);
  return s ? <span className="line-clamp-3 break-words" title={s}>{s}</span> : <span className="text-[#55555C] italic">none</span>;
}

// Dry-run: what each copy will look like after the merge, side by side with what it has now.
function MergePreview({ kind, preview }) {
  const Icon = kind === 'character' ? Users : MapPin;
  const inSel = preview.members.filter(m => m.in_selection);
  const pulled = preview.members.filter(m => !m.in_selection);
  const Row = ({ m }) => (
    <div className="rounded-lg border border-white/[0.06] bg-black/30 p-2.5" data-testid={`preview-member-${m.id}`}>
      <div className="flex items-center gap-2 mb-2">
        <div className="w-8 h-8 rounded bg-[#0a0a0e] border border-white/[0.08] overflow-hidden shrink-0 flex items-center justify-center">
          {imgOf(kind, m) ? <img src={getImageUrl(imgOf(kind, m))} alt="" className="w-full h-full object-cover" /> : <Icon className="w-3.5 h-3.5 text-[#55555C]" />}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-xs text-white font-medium truncate">{m.name} <span className="text-[#55555C] font-normal">· {m.project_name}</span></p>
          <p className="text-[10px] text-[#8A8A93]">{m.scene_refs} scene ref{m.scene_refs === 1 ? '' : 's'}{m.action === 'delete' ? ` will point to "${preview.primary.name}"` : ' keep working'}</p>
        </div>
        {m.action === 'delete'
          ? <span className="text-[9px] px-1.5 py-px rounded-full bg-red-500/10 text-red-400 border border-red-500/30 flex items-center gap-1"><Trash2 className="w-2.5 h-2.5" /> removed (same project)</span>
          : <span className="text-[9px] px-1.5 py-px rounded-full bg-[#10B981]/10 text-[#10B981] border border-[#10B981]/25">becomes synced copy</span>}
      </div>
      {m.action === 'link' && (m.changes.length === 0
        ? <p className="text-[10px] text-[#55555C]">Already identical to the primary — nothing changes.</p>
        : (
          <div className="grid grid-cols-[110px_1fr_1fr] gap-x-2 gap-y-1 text-[10px]" data-testid={`preview-changes-${m.id}`}>
            <span className="text-[#55555C] uppercase tracking-wider">Field</span><span className="text-[#55555C] uppercase tracking-wider">Now</span><span className="text-[#55555C] uppercase tracking-wider">After merge</span>
            {m.changes.map(c => (
              <Fragment key={c.field}>
                <span className="text-[#8A8A93] truncate">{FIELD_LABELS[c.field] || c.field}</span>
                <span className="text-red-300/80"><DiffValue field={c.field} value={c.from} /></span>
                <span className="text-[#10B981]"><DiffValue field={c.field} value={c.to} /></span>
              </Fragment>
            ))}
          </div>
        ))}
    </div>
  );
  return (
    <div className="mb-3 space-y-2" data-testid="merge-preview">
      <p className="text-[11px] text-[#8A8A93]">Keeping <span className="text-white font-semibold">{preview.primary.name}</span> <span className="text-[#55555C]">({preview.primary.project_name})</span> — {preview.members.length} record{preview.members.length === 1 ? '' : 's'} affected.</p>
      {inSel.map(m => <Row key={m.id} m={m} />)}
      {pulled.length > 0 && (
        <>
          <p className="text-[10px] text-[#8A8A93] pt-1">Also pulled in (already linked to one of the above):</p>
          {pulled.map(m => <Row key={m.id} m={m} />)}
        </>
      )}
    </div>
  );
}

function MergeGroup({ kind, group, onMerged }) {
  const items = group.items;
  const [included, setIncluded] = useState(() => new Set(items.map(x => x.id)));
  const [primary, setPrimary] = useState(items[0]?.id);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const [preview, setPreview] = useState(null);
  const Icon = kind === 'character' ? Users : MapPin;
  const count = [...included].length;

  const toggle = (id) => setIncluded(prev => {
    const n = new Set(prev);
    if (n.has(id)) n.delete(id); else n.add(id);
    if (!n.has(primary)) setPrimary([...n][0]);
    setPreview(null);
    return n;
  });

  const loadPreview = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/workspace/${pluralKind(kind)}/merge/preview`, { primary_id: primary, ids: [...included] });
      setPreview(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Could not build preview'); }
    finally { setBusy(false); }
  };

  const merge = async () => {
    setBusy(true);
    try {
      const res = await api.post(`/workspace/${pluralKind(kind)}/merge`, { primary_id: primary, ids: [...included] });
      setDone(res.data);
      const linked = res.data.linked?.length || 0, removed = res.data.deleted?.length || 0;
      toast.success(`Merged into "${res.data.primary?.name}"${removed ? ` · ${removed} duplicate${removed > 1 ? 's' : ''} removed` : ''}${linked ? ` · now one identity across ${linked + 1} projects` : ''}`, { duration: 6000 });
      onMerged(res.data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Merge failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className={`rounded-xl border p-3 ${done ? 'border-[#10B981]/30 bg-[#10B981]/5' : 'border-white/[0.08] bg-[#141418]'}`} data-testid={`merge-group-${items[0]?.id}`}>
      <div className="flex items-center justify-between gap-2 mb-2">
        <p className="text-xs text-[#8A8A93]">
          {items.length} records{group.already_merged ? <span className="ml-2 text-[10px] px-1.5 py-px rounded-full bg-[#10B981]/15 text-[#10B981] border border-[#10B981]/30">already one identity</span> : null}
        </p>
        {done ? (
          <span className="text-xs text-[#10B981] flex items-center gap-1" data-testid="merge-done"><Check className="w-3.5 h-3.5" /> Merged</span>
        ) : preview ? (
          <div className="flex items-center gap-2">
            <button data-testid={`merge-back-btn-${items[0]?.id}`} onClick={() => setPreview(null)} disabled={busy} className="px-3 py-1.5 rounded-lg text-xs text-[#8A8A93] hover:text-white border border-white/[0.08]">Back</button>
            <button data-testid={`merge-confirm-btn-${items[0]?.id}`} onClick={merge} disabled={busy}
              className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#FF4500] text-white hover:bg-[#E03C00] disabled:opacity-40 flex items-center gap-1.5">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <GitMerge className="w-3.5 h-3.5" />} Confirm merge
            </button>
          </div>
        ) : (
          <button data-testid={`merge-group-btn-${items[0]?.id}`} onClick={loadPreview} disabled={busy || count < 2}
            className="px-3 py-1.5 rounded-lg text-xs font-semibold bg-[#FF4500] text-white hover:bg-[#E03C00] disabled:opacity-40 flex items-center gap-1.5">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Eye className="w-3.5 h-3.5" />} Preview merge ({count})
          </button>
        )}
      </div>
      {preview && !done && <MergePreview kind={kind} preview={preview} />}
      <div className="space-y-1.5">
        {items.map(x => {
          const on = included.has(x.id);
          const isPrimary = primary === x.id;
          return (
            <div key={x.id} data-testid={`merge-item-${x.id}`}
              className={`flex items-center gap-2.5 p-2 rounded-lg border transition-colors ${isPrimary && on ? 'border-[#FF4500]/50 bg-[#FF4500]/5' : 'border-white/[0.05] bg-black/20'} ${on ? '' : 'opacity-50'}`}>
              <input type="checkbox" checked={on} disabled={!!done} onChange={() => toggle(x.id)} data-testid={`merge-include-${x.id}`} className="accent-[#FF4500] w-4 h-4 shrink-0" />
              <div className={`${kind === 'character' ? 'w-10 h-10' : 'w-14 h-10'} rounded-md bg-[#0a0a0e] border border-white/[0.08] overflow-hidden shrink-0 flex items-center justify-center`}>
                {imgOf(kind, x) ? <img src={getImageUrl(imgOf(kind, x))} alt="" className="w-full h-full object-cover" /> : <Icon className="w-4 h-4 text-[#55555C]" />}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-xs text-white font-medium truncate">{x.name}</p>
                <p className="text-[10px] text-[#8A8A93] truncate flex items-center gap-1"><FolderOpen className="w-3 h-3" /> {x.project_name || '—'}{group.reasons?.[x.id] ? ` · ${group.reasons[x.id]}` : ''}</p>
              </div>
              <button data-testid={`merge-primary-${x.id}`} disabled={!on || !!done} onClick={() => setPrimary(x.id)} title="Keep this one as the primary"
                className={`px-2 py-1 rounded-md text-[10px] font-semibold flex items-center gap-1 border transition-colors disabled:opacity-40 ${isPrimary ? 'bg-[#FF4500]/15 text-[#FF4500] border-[#FF4500]/40' : 'text-[#8A8A93] border-white/[0.08] hover:text-white'}`}>
                <Crown className="w-3 h-3" /> {isPrimary ? 'Primary' : 'Keep'}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Slide-over: either scans the workspace for duplicates (groups=null) or merges a manual selection (groups given).
export function MergeDrawer({ kind, groups: given, onClose, onMerged }) {
  const [groups, setGroups] = useState(given || null);
  const [loading, setLoading] = useState(!given);
  useEffect(() => {
    if (given) return;
    api.get(`/workspace/${pluralKind(kind)}/duplicates`).then(r => setGroups((r.data.groups || []).filter(g => !g.already_merged)))
      .catch(() => { toast.error('Could not scan for duplicates'); setGroups([]); }).finally(() => setLoading(false));
  }, [kind, given]);

  return createPortal(
    <div className="fixed inset-0 z-[200] bg-black/60 backdrop-blur-[2px]" data-testid={`merge-drawer-${kind}`} onClick={onClose}>
      <div data-testid="slide-over-panel" className="absolute right-0 top-0 h-full w-full max-w-[640px] bg-[#0E0E12] border-l border-white/10 shadow-2xl flex flex-col animate-slide-in-right p-5" onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-1">
          <p className="text-sm font-semibold text-white flex items-center gap-2"><GitMerge className="w-4 h-4 text-[#FF4500]" /> {given ? `Merge selected ${pluralKind(kind)}` : `Duplicate ${pluralKind(kind)}`}</p>
          <button onClick={onClose} className="p-1 rounded hover:bg-white/10 text-[#8A8A93]" data-testid="merge-drawer-close"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-[11px] text-[#8A8A93] mb-4">The primary keeps its name and look. Duplicates in the same project are removed (scenes re-pointed); copies in other projects become the same identity and stay in sync from now on.</p>
        <div className="overflow-y-auto flex-1 space-y-3">
          {loading ? <div className="flex justify-center py-10"><Loader2 className="w-5 h-5 animate-spin text-[#8A8A93]" /></div>
            : !groups || groups.length === 0 ? <p className="text-xs text-[#55555C] text-center py-10" data-testid="merge-empty">No duplicates left — merged {pluralKind(kind)} already show as one identity in the grid.</p>
            : groups.map((g, i) => <MergeGroup key={g.items[0]?.id || i} kind={kind} group={g} onMerged={onMerged} />)}
        </div>
      </div>
    </div>,
    document.body
  );
}
